import {
  readNativeCornerCurve,
  readRenderedCornerRadius,
  roundToHalfPixel,
  type NativeGlassRect,
} from "./native-glass-geometry";
export {
  classifyRectsDelta,
  type BatchDelta,
  type NativeGlassRect,
} from "./native-glass-geometry";
export {
  ACTIVE_TRANSITION_MS,
  readAnimationWindow,
  transitionCanMoveSurfaces,
} from "./native-glass-timing";
import {
  compareStackPaths,
  readSurfaceChain,
  type StackLevel,
} from "./native-glass-stack";
// DOM surface measurement and marks; use-native-glass-surfaces owns IPC.
import {
  isNativeSurfaceLevel,
  isSurfaceRole,
  type NativeSurfaceLevel,
  type SurfaceRole,
} from "@/components/ui/surface-contract";
import { detectZoomRectSemantics } from "@/lib/zoom-rect-semantics";
import {
  applyStackedMarks,
  computeStackRelations,
  isStackedPopupRole,
  readPaperRects,
  STACKED_ATTR,
  syncStackMark,
} from "./native-glass-densify";
import {
  isNativeGlassModelFrameGroup,
  readNativeGlassModelFrameRect,
  shouldRetainNativeGlassModelFrames,
} from "./native-glass-model-frames";
import { readPerfDebugFlag } from "./perf-debug-flags";
import {
  readNativeGlassMorph,
  resolveNativeGlassMeasuredRect,
} from "./native-glass-motion";
const MAX_NATIVE_SURFACES = 96;
export const SURFACE_ROLE_ATTR = "data-denker-surface-role";
export const NATIVE_LEVEL_ATTR = "data-denker-native-level";
export const NATIVE_GROUP_ATTR = "data-denker-native-group";
export const NATIVE_SURFACE_ATTR = "data-denker-native-surface";
const NATIVE_CHILD_ATTR = "data-denker-native-surface-child";
const NATIVE_MARK_GROUP_ATTR = "data-denker-native-surface-group";
export const GLASS_FORCE_ATTR = "data-denker-glass-force";
/** A plate's opacity (0–1); native fades to it and hides the plate at 0. */
export const NATIVE_OPACITY_ATTR = "data-denker-native-opacity";
const GLASS_VARIANT_ATTR = "data-denker-glass-variant";
const INPUT_BAR_SHELL_CLASS = "denker-input-bar-shell";
const SURFACE_ROLE_PRIORITY: Record<SurfaceRole, number> = {
  backdrop: -1,
  chrome: 0,
  toolbar: 1,
  rail: 2,
  panel: 3,
  "side-panel": 3,
  modal: 6,
  input: 4,
  progress: 5,
  menu: 6,
  frame: 7,
};
/** Bottom → top: the wall's full-window frost sits under every plate. */
const SURFACE_STACK_PRIORITY: Record<SurfaceRole, number> = {
  backdrop: -1,
  frame: 0,
  panel: 1,
  "side-panel": 1,
  rail: 2,
  toolbar: 3,
  input: 4,
  chrome: 5,
  progress: 6,
  modal: 7,
  menu: 8,
};

type NativeGlassCandidate = NativeGlassRect & {
  element: HTMLElement;
  level: NativeSurfaceLevel;
  order: number;
  stackPath: StackLevel[];
  offscreen: boolean;
};

export const clearNativeSurfaceMarks = () => {
  for (const element of document.querySelectorAll<HTMLElement>(
    `[${NATIVE_SURFACE_ATTR}],[${NATIVE_CHILD_ATTR}],[${NATIVE_MARK_GROUP_ATTR}],[${STACKED_ATTR}]`,
  )) {
    element.removeAttribute(NATIVE_SURFACE_ATTR);
    element.removeAttribute(NATIVE_CHILD_ATTR);
    element.removeAttribute(NATIVE_MARK_GROUP_ATTR);
    element.removeAttribute(STACKED_ATTR);
  }
};

const readNativeLevel = (element: HTMLElement): NativeSurfaceLevel => {
  const raw = element.getAttribute(NATIVE_LEVEL_ATTR);
  return isNativeSurfaceLevel(raw) ? raw : "auto";
};

const readNativeGroup = (element: HTMLElement) => {
  const group = element.getAttribute(NATIVE_GROUP_ATTR)?.trim();
  return group || undefined;
};

/**
 * Debug override from the glass audit overlay: an ancestor wrapper can force
 * a surface off the native path ("css") or onto the NSVisualEffectView
 * fallback ("visual-effect") for side-by-side comparison.
 */
const readGlassForce = (
  element: HTMLElement,
): "css" | "visual-effect" | undefined => {
  const value = element
    .closest<HTMLElement>(`[${GLASS_FORCE_ATTR}]`)
    ?.getAttribute(GLASS_FORCE_ATTR)
    ?.trim();
  return value === "css" || value === "visual-effect" ? value : undefined;
};

/**
 * Debug override from the glass audit overlay: an ancestor wrapper can pin a
 * specific NSGlassEffectView private variant (0-23) on a surface so the
 * variants view can render every material side by side. Production markup
 * never sets this attribute.
 */
const readGlassVariant = (element: HTMLElement): number | undefined => {
  const value = element
    .closest<HTMLElement>(`[${GLASS_VARIANT_ATTR}]`)
    ?.getAttribute(GLASS_VARIANT_ATTR)
    ?.trim();
  if (!value) return undefined;
  const variant = Number.parseInt(value, 10);
  return Number.isInteger(variant) && variant >= 0 && variant <= 23
    ? variant
    : undefined;
};

/** A plate's opacity, clamped to 0–1; omitted, native keeps the plate at 1. */
const readNativeOpacity = (element: HTMLElement): number | undefined => {
  const raw = element.getAttribute(NATIVE_OPACITY_ATTR);
  if (raw === null) return undefined;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : undefined;
};

type SurfaceMark = {
  attr: typeof NATIVE_SURFACE_ATTR | typeof NATIVE_CHILD_ATTR;
  role: SurfaceRole;
  group: string | undefined;
};

/**
 * Reconcile the DOM mark attributes against the desired set with minimal
 * writes. Attribute WRITES invalidate style (surface-system.css keys heavy
 * rules on these marks) so the steady state — same membership, positions
 * moved — must perform zero writes. Reads are free.
 */
const applyNativeSurfaceMarks = (desired: Map<HTMLElement, SurfaceMark>) => {
  for (const element of document.querySelectorAll<HTMLElement>(
    `[${NATIVE_SURFACE_ATTR}],[${NATIVE_CHILD_ATTR}],[${NATIVE_MARK_GROUP_ATTR}],[${STACKED_ATTR}]`,
  )) {
    if (!desired.has(element)) {
      element.removeAttribute(NATIVE_SURFACE_ATTR);
      element.removeAttribute(NATIVE_CHILD_ATTR);
      element.removeAttribute(NATIVE_MARK_GROUP_ATTR);
      element.removeAttribute(STACKED_ATTR);
    }
  }
  for (const [element, mark] of desired) {
    const otherAttr =
      mark.attr === NATIVE_SURFACE_ATTR
        ? NATIVE_CHILD_ATTR
        : NATIVE_SURFACE_ATTR;
    if (element.hasAttribute(otherAttr)) element.removeAttribute(otherAttr);
    if (element.getAttribute(mark.attr) !== mark.role) {
      element.setAttribute(mark.attr, mark.role);
    }
    if (mark.attr === NATIVE_CHILD_ATTR) {
      // A popup drawn on its host's plate sits on that glass by construction.
      syncStackMark(element, isStackedPopupRole(mark.role));
    }
    if (mark.group) {
      if (element.getAttribute(NATIVE_MARK_GROUP_ATTR) !== mark.group) {
        element.setAttribute(NATIVE_MARK_GROUP_ATTR, mark.group);
      }
    } else if (element.hasAttribute(NATIVE_MARK_GROUP_ATTR)) {
      element.removeAttribute(NATIVE_MARK_GROUP_ATTR);
    }
  }
};

const scaleCornerRadius = (
  radius: number | undefined,
  zoomScale: number,
): number | undefined =>
  radius === undefined || zoomScale === 1 ? radius : radius * zoomScale;

/**
 * The canvas sharp-zoom workaround (`.safari-zoom-active`,
 * canvas-viewport.tsx) is the app's only CSS `zoom:` source. On engines
 * that report getBoundingClientRect divided by the effective zoom
 * (WebKit < 26.4 — see zoom-rect-semantics.ts), surfaces inside the zoomed
 * viewport must be scaled back to visual coordinates before they are
 * shipped to AppKit, or the native plates render offset from the DOM.
 */
const resolveCanvasZoomCompensation = (): {
  root: HTMLElement;
  zoom: number;
} | null => {
  const container = document.querySelector<HTMLElement>(".safari-zoom-active");
  if (!container) return null;
  const zoom = Number.parseFloat(container.style.getPropertyValue("--rf-zoom"));
  if (!Number.isFinite(zoom) || zoom <= 0 || zoom === 1) return null;
  if (detectZoomRectSemantics() !== "unscaled") return null;
  const root = container.querySelector<HTMLElement>(".react-flow__viewport");
  if (!root) return null;
  return { root, zoom };
};

let lastNativeSurfaceRoleSignature = "";

export type CollectSurfaceRectsOptions = {
  includeFrameSurfaces?: boolean;
};

export const collectSurfaceRects = (
  options: CollectSurfaceRectsOptions = {},
) => {
  const includeFrameSurfaces = options.includeFrameSurfaces ?? true;
  const seen = new Set<HTMLElement>();
  const selected: HTMLElement[] = [];
  const candidates: NativeGlassCandidate[] = [];
  const selectedCandidates: NativeGlassCandidate[] = [];
  let nativeCount = 0;
  const desiredMarks = new Map<HTMLElement, SurfaceMark>();
  const styleCache = new Map<HTMLElement, CSSStyleDeclaration>();

  const resolveStyle = (target: HTMLElement) => {
    let style = styleCache.get(target);
    if (!style) {
      style = getComputedStyle(target);
      styleCache.set(target, style);
    }
    return style;
  };

  const zoomCompensation = resolveCanvasZoomCompensation();

  const addCandidate = (
    element: HTMLElement,
    role: SurfaceRole,
    order: number,
  ) => {
    if (seen.has(element)) return;
    seen.add(element);
    if (role === "frame" && !includeFrameSurfaces) return;
    if (element.hasAttribute("data-native-glass-suspended")) return;
    const forcedMode = readGlassForce(element);
    if (forcedMode === "css") return;
    const floatingFrame = element.closest<HTMLElement>(
      "[data-floating-frame-id]",
    );
    const group =
      floatingFrame && role === "frame"
        ? `floating-frame:${floatingFrame.dataset.floatingFrameId}`
        : readNativeGroup(element);
    const modelFrameRect =
      role === "frame" ? readNativeGlassModelFrameRect(group) : undefined;
    if (
      role === "frame" &&
      isNativeGlassModelFrameGroup(group) &&
      !modelFrameRect
    ) {
      return;
    }
    if (modelFrameRect) {
      const chain = readSurfaceChain(
        element,
        resolveStyle,
        modelFrameRect.stackPath,
      );
      if (!chain.visible) return;
      const rect = modelFrameRect;
      const offscreen =
        rect.x + rect.width <= 0 ||
        rect.y + rect.height <= 0 ||
        rect.x >= window.innerWidth ||
        rect.y >= window.innerHeight;
      candidates.push({
        element,
        group,
        forcedMode,
        variant: readGlassVariant(element),
        level: readNativeLevel(element),
        order,
        stackPath: chain.stackPath,
        offscreen: offscreen && !shouldRetainNativeGlassModelFrames(),
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
        cornerRadius: rect.cornerRadius,
        role,
        morph: readNativeGlassMorph(element),
      });
      return;
    }

    const chain = readSurfaceChain(element, resolveStyle);
    if (!chain.visible) return;

    const domRect = element.getBoundingClientRect();
    const zoomScale = zoomCompensation?.root.contains(element)
      ? zoomCompensation.zoom
      : 1;
    const rect = resolveNativeGlassMeasuredRect(element, domRect, zoomScale);
    if (rect.width < 8 || rect.height < 8) return;
    const offscreen =
      rect.right <= 0 ||
      rect.bottom <= 0 ||
      rect.left >= window.innerWidth ||
      rect.top >= window.innerHeight;
    candidates.push({
      element,
      group,
      forcedMode,
      variant: readGlassVariant(element),
      level: readNativeLevel(element),
      order,
      stackPath: chain.stackPath,
      offscreen,
      x: roundToHalfPixel(rect.x),
      y: roundToHalfPixel(rect.y),
      width: roundToHalfPixel(rect.width),
      height: roundToHalfPixel(rect.height),
      cornerRadius: element.classList.contains(INPUT_BAR_SHELL_CLASS)
        ? undefined
        : scaleCornerRadius(
            readRenderedCornerRadius(element, domRect, resolveStyle),
            zoomScale,
          ),
      cornerCurve: readNativeCornerCurve(resolveStyle(element)),
      role,
      opacity: readNativeOpacity(element),
      morph: readNativeGlassMorph(element),
    });
  };

  // Intel canvas surfaces use the lightweight CSS material. Exclude every
  // React Flow viewport, including floating canvases: native AppKit plates
  // cannot follow their CSS transforms synchronously during dragging.
  const intelExcludesViewportSurfaces =
    document.documentElement.dataset.denkerIntel === "true";

  let order = 0;
  for (const element of document.querySelectorAll<HTMLElement>(
    `[${SURFACE_ROLE_ATTR}]`,
  )) {
    if (
      intelExcludesViewportSurfaces &&
      element.closest(".react-flow__viewport")
    ) {
      order += 1;
      continue;
    }
    const role = element.getAttribute(SURFACE_ROLE_ATTR);
    if (isSurfaceRole(role)) {
      addCandidate(
        element,
        // Keep the input bar's rail identity for collection and ordering.
        // Native styling gives the input-bar group the menu glass finish.
        element.classList.contains(INPUT_BAR_SHELL_CLASS) ? "rail" : role,
        order,
      );
    }
    order += 1;
  }

  candidates.sort((a, b) => {
    if (a.element.contains(b.element)) return -1;
    if (b.element.contains(a.element)) return 1;
    const priorityDelta =
      SURFACE_ROLE_PRIORITY[a.role] - SURFACE_ROLE_PRIORITY[b.role];
    return priorityDelta === 0 ? a.order - b.order : priorityDelta;
  });

  for (const candidate of candidates) {
    const containingSurface = selected.find((element) =>
      element.contains(candidate.element),
    );

    if (candidate.level === "child") {
      if (containingSurface) {
        desiredMarks.set(candidate.element, {
          attr: NATIVE_CHILD_ATTR,
          role: candidate.role,
          group: candidate.group,
        });
      }
      continue;
    }

    const explicitNative =
      candidate.level === "root" || candidate.level === "island";
    if (containingSurface && !explicitNative) {
      desiredMarks.set(candidate.element, {
        attr: NATIVE_CHILD_ATTR,
        role: candidate.role,
        group: candidate.group,
      });
      continue;
    }
    if (!candidate.offscreen && nativeCount >= MAX_NATIVE_SURFACES) continue;
    selected.push(candidate.element);
    desiredMarks.set(candidate.element, {
      attr: NATIVE_SURFACE_ATTR,
      role: candidate.role,
      group: candidate.group,
    });
    if (!candidate.offscreen) {
      nativeCount += 1;
      selectedCandidates.push(candidate);
    }
  }

  applyNativeSurfaceMarks(desiredMarks);

  const sortedCandidates = [...selectedCandidates].sort((a, b) => {
    const priorityDelta =
      SURFACE_STACK_PRIORITY[a.role] - SURFACE_STACK_PRIORITY[b.role];
    if (priorityDelta !== 0) return priorityDelta;
    const stackDelta = compareStackPaths(a.stackPath, b.stackPath);
    return stackDelta === 0 ? a.order - b.order : stackDelta;
  });

  // Permanent floating chrome (isStackExempt) never densifies;
  // computeStackRelations lets it stack only a popup opened over it.
  const relations = computeStackRelations(sortedCandidates, {
    paperRects: readPaperRects(sortedCandidates, resolveStyle),
  });
  applyStackedMarks(sortedCandidates, relations);

  // Offscreen surfaces keep their native mark but are excluded from
  // selectedCandidates — applyStackedMarks never visits them, so clear any
  // stale stacked attr they may carry from a previous pass. Native
  // children are reconciled by applyNativeSurfaceMarks.
  const stackedScope = new Set(
    sortedCandidates.map((candidate) => candidate.element),
  );
  for (const [element, mark] of desiredMarks) {
    if (mark.attr === NATIVE_SURFACE_ATTR && !stackedScope.has(element)) {
      syncStackMark(element, false);
    }
  }

  if (readPerfDebugFlag("nativeSurfaceRoles")) {
    const roleCounts = sortedCandidates.reduce<Record<string, number>>(
      (counts, candidate) => {
        counts[candidate.role] = (counts[candidate.role] ?? 0) + 1;
        return counts;
      },
      {},
    );
    const signature = Object.entries(roleCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([role, count]) => `${role}:${count}`)
      .join(",");
    if (signature !== lastNativeSurfaceRoleSignature) {
      lastNativeSurfaceRoleSignature = signature;
      console.log(
        `[perf] native-surfaces total=${sortedCandidates.length} roles=${signature || "none"}`,
      );
    }
  }

  return sortedCandidates.map(
    ({
      element: _element,
      level: _level,
      order: _order,
      stackPath: _stackPath,
      offscreen: _offscreen,
      ...rect
    }) => rect,
  );
};
